import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Film, Clock, HardDrive, Loader2, Trash2, Radio, Calendar, Code2, Copy, Check, Link2, X, UploadCloud, Download, FileVideo, AlertCircle, Pencil, Infinity, Sparkles, Layers, CheckCircle2, XCircle, Plus, ListPlus, Tv, Users, Scissors } from "lucide-react";
import { ClipDialog } from "@/components/ClipDialog";
import api, { API } from "@/lib/api";
import { buildDownloadUrl, safeVideoFilename } from "@/lib/download";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import HlsPlayer from "@/components/HlsPlayer";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmtDuration = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const fmtSize = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb} MB`);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function Videos() {
  const { user } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [urlEdit, setUrlEdit] = useState(null); // recording being edited
  const [urlValue, setUrlValue] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedSize, setEmbedSize] = useState("640");
  const [copied, setCopied] = useState(false);
  const [confirmRec, setConfirmRec] = useState(null);
  const [clipRec, setClipRec] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadName, setUploadName] = useState("");
  const [confirmUpload, setConfirmUpload] = useState(null);
  const [deletingUpload, setDeletingUpload] = useState(false);
  const [quota, setQuota] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const isAdmin = user?.role === "admin";
  const [churches, setChurches] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allPlaylists, setAllPlaylists] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("channel"); // 'channel' | 'account'
  const [bulkChannel, setBulkChannel] = useState("");
  const [bulkAccount, setBulkAccount] = useState("");
  const [bulkFiles, setBulkFiles] = useState([]); // {id, file, name, size, progress, status, error}
  const [bulkRunning, setBulkRunning] = useState(false);
  // Move-to-playlist (admin)
  const [moveTarget, setMoveTarget] = useState(null); // the upload being moved
  const [moveSelected, setMoveSelected] = useState("");
  const [moveNewName, setMoveNewName] = useState("");
  const [moving, setMoving] = useState(false);
  const [theme, setTheme] = useState({
    accent: (user?.accent_color || "#F59E0B"),
    text: "#0B0F17",
    title: user?.organization || "",
    layout: "side",
  });

  const buildEmbedSrc = () => {
    const base = `${window.location.origin}/embed/library/${user?.user_id}`;
    const p = new URLSearchParams();
    if (theme.accent) p.set("accent", theme.accent.replace("#", ""));
    if (theme.text) p.set("text", theme.text.replace("#", ""));
    if (theme.title.trim()) p.set("title", theme.title.trim());
    if (theme.layout === "below") p.set("layout", "below");
    const qs = p.toString();
    return qs ? `${base}?${qs}` : base;
  };
  const embedSrc = buildEmbedSrc();
  const embedMaxWidth = embedSize === "full" ? "100%" : `${embedSize}px`;
  const embedCode = `<div style="position:relative;width:100%;max-width:${embedMaxWidth};margin:0 auto;">
  <div style="position:relative;padding-top:56.25%;">
    <iframe src="${embedSrc}" allow="autoplay; fullscreen; encrypted-media" allowfullscreen frameborder="0" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;border-radius:12px;"></iframe>
  </div>
</div>`;

  const load = async () => {
    try {
      const res = await api.get("/recordings");
      setRecordings(res.data);
    } catch (e) {
      toast.error("Failed to load recordings");
    } finally {
      setLoading(false);
    }
  };

  const loadUploads = async () => {
    try { setUploads((await api.get("/uploads")).data); } catch (e) { /* silent */ }
  };

  const loadQuota = async () => {
    try { setQuota((await api.get("/usage/storage")).data); } catch (e) { /* silent */ }
  };

  const loadChurches = async () => {
    try { setChurches((await api.get("/admin/church-apps")).data); } catch (e) { /* silent */ }
  };

  const loadAccounts = async () => {
    try {
      const res = await api.get("/admin/users");
      setAccounts((res.data || []).filter((u) => u.role !== "admin"));
    } catch (e) { /* silent */ }
  };

  const loadAllPlaylists = async () => {
    try { setAllPlaylists((await api.get("/playlists")).data || []); } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); loadUploads(); loadQuota(); loadAllPlaylists(); }, [user?.role]);
  useEffect(() => { if (isAdmin) { loadChurches(); loadAccounts(); } }, [isAdmin]);

  // Poll while any upload is still processing/uploading so the status updates live.
  useEffect(() => {
    const pending = uploads.some((u) => ["uploading", "processing"].includes(u.status));
    if (!pending) return;
    const t = setInterval(loadUploads, 4000);
    return () => clearInterval(t);
  }, [uploads]);

  const CHUNK_SIZE = 5 * 1024 * 1024; // legacy (kept for reference)

  // Grab a poster frame in the browser (no server transcode with direct upload).
  const generatePoster = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.src = url;
      const cleanup = () => { try { URL.revokeObjectURL(url); } catch (e) {} };
      video.onloadeddata = () => {
        const seekTo = Math.min(1, (video.duration || 2) / 2);
        try { video.currentTime = seekTo; } catch (e) { resolve(null); cleanup(); }
      };
      video.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          const w = 640;
          const scale = w / (video.videoWidth || w);
          canvas.width = w;
          canvas.height = Math.round((video.videoHeight || 360) * scale);
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
          resolve({ dataUrl, duration: Math.round(video.duration || 0) });
        } catch (e) { resolve(null); } finally { cleanup(); }
      };
      video.onerror = () => { resolve(null); cleanup(); };
    } catch (e) { resolve(null); }
  });

  // Shared multipart upload: start session -> PUT parts directly to R2 -> complete.
  // Returns on success; throws Error(message) on failure. `churchAppId` (admin) tags
  // the upload to that channel's linked account.
  const runMultipartUpload = async (file, { churchAppId = null, targetOwnerId = null, onProgress } = {}) => {
    const title = file.name.replace(/\.[^.]+$/, "");
    let uploadId = null;
    try {
      const startRes = await api.post("/multipart/start", {
        title, filename: file.name, size: file.size, content_type: file.type || "video/mp4",
        church_app_id: churchAppId || undefined,
        target_owner_id: targetOwnerId || undefined,
      });
      const { upload_id, part_size, parts } = startRes.data;
      uploadId = upload_id;
      const done = [];
      let sent = 0;
      for (const p of parts) {
        const startByte = (p.part_number - 1) * part_size;
        const blob = file.slice(startByte, Math.min(startByte + part_size, file.size));
        const res = await axios.put(p.url, blob, {
          onUploadProgress: (ev) => {
            onProgress?.(Math.min(99, Math.round(((sent + ev.loaded) / file.size) * 100)));
          },
        });
        const etag = res.headers.etag || res.headers.ETag;
        if (!etag) throw new Error("Cloud storage did not return an ETag — check R2 bucket CORS (ExposeHeaders: ETag).");
        done.push({ PartNumber: p.part_number, ETag: etag });
        sent += blob.size;
        onProgress?.(Math.min(99, Math.round((sent / file.size) * 100)));
      }
      const poster = await generatePoster(file);
      await api.post("/multipart/complete", {
        upload_id, parts: done,
        thumbnail_base64: poster?.dataUrl || null,
        duration_seconds: poster?.duration || 0,
      });
      onProgress?.(100);
    } catch (err) {
      if (uploadId) { try { await api.post("/multipart/abort", { upload_id: uploadId }); } catch (e) { /* ignore */ } }
      const msg = err.response?.data?.detail
        || (err.request && !err.response
          ? "Upload failed — couldn't reach cloud storage. Please check your connection and try again."
          : err.message || "Upload failed. Please try again.");
      throw new Error(msg);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (!/\.mp4$/i.test(file.name) && file.type !== "video/mp4") {
      toast.error("Please choose an MP4 video file");
      return;
    }
    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error("File exceeds the 5 GB limit");
      return;
    }
    setUploading(true);
    setProgress(0);
    setUploadName(file.name);
    try {
      await runMultipartUpload(file, { onProgress: setProgress });
      toast.success("Upload complete — saved to your cloud library");
      await loadUploads();
      loadQuota();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setProgress(0);
      setUploadName("");
    }
  };

  // ---- Admin bulk upload (multiple files to one channel or account) ----
  const selectedChurch = churches.find((c) => c.id === bulkChannel) || null;
  const channelHasAccount = !!selectedChurch?.owner_id;
  const bulkTargetReady = bulkMode === "channel" ? (!!bulkChannel && channelHasAccount) : !!bulkAccount;

  const addBulkFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const valid = [];
    for (const file of picked) {
      if (!/\.mp4$/i.test(file.name) && file.type !== "video/mp4") { toast.error(`${file.name}: not an MP4 — skipped`); continue; }
      if (file.size > 5 * 1024 * 1024 * 1024) { toast.error(`${file.name}: exceeds the 5 GB limit — skipped`); continue; }
      valid.push({ id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`, file, name: file.name, size: file.size, progress: 0, status: "queued", error: "" });
    }
    if (valid.length) setBulkFiles((prev) => [...prev, ...valid]);
  };

  const removeBulkFile = (id) => setBulkFiles((prev) => prev.filter((f) => f.id !== id));

  const bulkTargetLabel = () => bulkMode === "channel"
    ? (selectedChurch?.name || "channel")
    : (accounts.find((a) => a.user_id === bulkAccount)?.organization || accounts.find((a) => a.user_id === bulkAccount)?.name || "account");

  const startBulkUpload = async () => {
    if (!bulkTargetReady) {
      toast.error(bulkMode === "channel" ? "Choose a channel with a linked account first" : "Choose a customer account first");
      return;
    }
    const queue = bulkFiles.filter((f) => f.status === "queued" || f.status === "failed");
    if (!queue.length) { toast.error("Add at least one video file"); return; }
    const targetOpts = bulkMode === "channel" ? { churchAppId: bulkChannel } : { targetOwnerId: bulkAccount };
    setBulkRunning(true);
    let okCount = 0, failCount = 0;
    for (const item of queue) {
      setBulkFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: "uploading", progress: 0, error: "" } : f)));
      try {
        await runMultipartUpload(item.file, {
          ...targetOpts,
          onProgress: (p) => setBulkFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, progress: p } : f))),
        });
        okCount++;
        setBulkFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: "complete", progress: 100 } : f)));
      } catch (err) {
        failCount++;
        setBulkFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: "failed", error: err.message } : f)));
      }
    }
    setBulkRunning(false);
    if (okCount) { toast.success(`${okCount} video${okCount === 1 ? "" : "s"} uploaded to ${bulkTargetLabel()}`); await loadUploads(); loadQuota(); loadAllPlaylists(); }
    if (failCount) toast.error(`${failCount} upload${failCount === 1 ? "" : "s"} failed — see the list`);
  };

  const closeBulk = () => {
    if (bulkRunning) return;
    setBulkOpen(false);
    setBulkFiles([]);
    setBulkChannel("");
    setBulkAccount("");
    setBulkMode("channel");
  };

  // ---- Move a video into one of its account's playlists (admin) ----
  const openMove = (item, kind = "upload") => {
    setMoveTarget({ ...item, _moveKind: kind });
    setMoveSelected("");
    setMoveNewName("");
  };
  const movePlaylists = moveTarget
    ? allPlaylists.filter((p) => p.owner_id === moveTarget.owner_id)
    : [];
  const doMove = async () => {
    if (!moveTarget) return;
    const makingNew = moveSelected === "__new__";
    if (makingNew && !moveNewName.trim()) { toast.error("Enter a name for the new playlist"); return; }
    if (!makingNew && !moveSelected) { toast.error("Choose a playlist"); return; }
    setMoving(true);
    try {
      let plId = moveSelected;
      let plName = movePlaylists.find((p) => p.id === moveSelected)?.name || "";
      if (makingNew) {
        const created = await api.post("/playlists", { name: moveNewName.trim(), owner_id: moveTarget.owner_id });
        plId = created.data.id;
        plName = created.data.name;
      }
      await api.post(`/playlists/${plId}/items`, { kind: moveTarget._moveKind || "upload", id: moveTarget.id });
      toast.success(`Added to “${plName}”`);
      setMoveTarget(null);
      loadAllPlaylists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add the video to that playlist");
    } finally {
      setMoving(false);
    }
  };

  const removeUpload = async (u) => {
    setDeletingUpload(true);
    try {
      await api.delete(`/uploads/${u.id}`);
      setUploads((list) => list.filter((x) => x.id !== u.id));
      toast.success("Video deleted from your library and Cloudflare R2");
      setConfirmUpload(null);
      loadQuota();
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setDeletingUpload(false);
    }
  };

  const openRename = (u) => { setRenameTarget(u); setRenameValue(u.title || u.original_filename || ""); };

  const saveRename = async () => {
    const title = renameValue.trim();
    if (!title) { toast.error("Title cannot be empty"); return; }
    setSavingRename(true);
    try {
      await api.patch(`/uploads/${renameTarget.id}`, { title });
      setUploads((list) => list.map((x) => (x.id === renameTarget.id ? { ...x, title } : x)));
      toast.success("Video renamed");
      setRenameTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    } finally {
      setSavingRename(false);
    }
  };

  const remove = async (rec) => {
    setDeleting(true);
    try {
      await api.delete(`/recordings/${rec.id}`);
      setRecordings((r) => r.filter((x) => x.id !== rec.id));
      toast.success("Video permanently deleted");
      setConfirmRec(null);
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const openUrlEdit = (rec) => { setUrlEdit(rec); setUrlValue(rec.playback_url || ""); };

  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      const res = await api.put(`/recordings/${urlEdit.id}`, { playback_url: urlValue.trim() });
      setRecordings((r) => r.map((x) => (x.id === urlEdit.id ? res.data : x)));
      toast.success("Video URL saved");
      setUrlEdit(null);
    } catch (e) {
      toast.error("Failed to save video URL");
    } finally {
      setSavingUrl(false);
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed — select the code manually"); }
  };

  // Most recently recorded first.
  const sortedRecordings = [...recordings].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Videos</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Upload videos to your cloud library, or publish broadcasts saved when a stream ends.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input data-testid="video-file-input" type="file" accept="video/mp4,.mp4" onChange={handleFile} className="hidden" id="video-upload-input" disabled={uploading} />
          <label
            htmlFor="video-upload-input"
            data-testid="upload-video-button"
            className={"font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition-all active:scale-[0.98] border " +
              (uploading ? "border-gray-700 text-gray-500 cursor-not-allowed" : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10 cursor-pointer")}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload Video
          </label>
          {isAdmin && (
            <button
              data-testid="bulk-upload-button"
              onClick={() => setBulkOpen(true)}
              className="font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition-all active:scale-[0.98] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            >
              <Layers className="h-4 w-4" /> Bulk upload
            </button>
          )}
          <button data-testid="get-embed-code-button" onClick={() => setEmbedOpen(true)} className="gold-gradient text-black font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 hover:brightness-110 transition-all active:scale-[0.98]">
            <Code2 className="h-4 w-4" /> Get website embed code
          </button>
        </div>
      </div>

      {/* Storage quota bar */}
      {quota && (() => {
        const unlimited = quota.unlimited;
        const pct = quota.percent || 0;
        const danger = !unlimited && pct >= 100;
        const warn = !unlimited && quota.warn;
        const usedLabel = quota.used_mb >= 1024 ? `${(quota.used_mb / 1024).toFixed(2)} GB` : `${quota.used_mb} MB`;
        const barColor = danger ? "bg-red-500" : warn ? "bg-amber-500" : "gold-gradient";
        const textColor = danger ? "text-red-400" : warn ? "text-amber-400" : (unlimited ? "text-amber-300" : "text-gray-300");
        return (
          <div data-testid="storage-quota-bar" className="mb-6 bg-card border border-gray-800 rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2.5 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <HardDrive className={"h-4 w-4 " + textColor} />
                <span className="text-sm font-semibold text-white">Cloud storage</span>
                <span className="text-xs text-gray-500">· {quota.videos} video{quota.videos === 1 ? "" : "s"}</span>
              </div>
              <span className={"text-sm font-medium " + textColor}>
                {unlimited ? (
                  <><span data-testid="quota-used">{usedLabel}</span> <span className="text-gray-500">used ·</span> <span className="inline-flex items-center gap-1"><Infinity className="h-4 w-4" /> Unlimited</span></>
                ) : (
                  <><span data-testid="quota-used">{usedLabel}</span> <span className="text-gray-500">of {quota.quota_gb} GB</span> · {pct}%</>
                )}
              </span>
            </div>
            {unlimited ? (
              <div className="h-2.5 w-full rounded-full bg-gradient-to-r from-amber-500/60 via-amber-400/40 to-amber-500/60" data-testid="quota-unlimited-bar" />
            ) : (
              <div className="h-2.5 w-full rounded-full bg-gray-800 overflow-hidden">
                <div className={"h-full rounded-full transition-all duration-500 " + barColor} style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
            )}
            {unlimited ? (
              <p className="text-xs mt-2.5 flex items-center gap-1.5 text-amber-300/80">
                <Sparkles className="h-3.5 w-3.5 shrink-0" /> Ascend Ultra includes unlimited priority 4K cloud archiving.
              </p>
            ) : (warn || danger) && (
              <p className={"text-xs mt-2.5 flex items-center gap-1.5 " + textColor}>
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {danger
                  ? "You've reached your plan's storage limit. Delete some videos or upgrade your plan to upload more."
                  : `You're using over ${quota.warn_percent}% of your storage. Consider upgrading your plan soon.`}
              </p>
            )}
          </div>
        );
      })()}

      {/* Upload progress */}
      {uploading && (
        <div data-testid="upload-progress" className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-amber-200 font-medium flex items-center gap-2 min-w-0">
              <FileVideo className="h-4 w-4 shrink-0" /> <span className="truncate">Uploading {uploadName}</span>
            </span>
            <span className="text-amber-300 font-mono shrink-0 ml-3">{progress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-800 overflow-hidden">
            <div className="h-full gold-gradient rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Uploaded videos table */}
      {uploads.length > 0 && (
        <div data-testid="uploads-section" className="mb-8 bg-card border border-gray-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-800 flex items-center gap-2">
            <UploadCloud className="h-4 w-4 text-amber-400" />
            <h2 className="font-display text-sm font-semibold">Uploaded videos</h2>
            <span className="text-xs text-gray-500">({uploads.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 text-xs uppercase tracking-wide border-b border-gray-800">
                <th className="px-5 py-3 font-medium">Video</th>
                <th className="px-5 py-3 font-medium">Size</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Uploaded</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>
                {uploads.map((u) => {
                  const ready = u.status === "ready";
                  const failed = u.status === "failed";
                  return (
                    <tr key={u.id} data-testid={`upload-row-${u.id}`} className="border-t border-gray-800/60 hover:bg-gray-800/30">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {ready && u.thumbnail_url ? (
                            <button
                              data-testid={`upload-thumb-${u.id}`}
                              onClick={() => setPlaying({ id: u.id, title: u.title || u.original_filename, playback_url: `${API}/media/${u.id}.mp4`, thumbnail_url: u.thumbnail_url })}
                              className="relative h-11 w-[74px] rounded-md overflow-hidden border border-gray-700 shrink-0 group bg-black"
                            >
                              <img src={`${API}/media/${u.id}.jpg`} alt="" loading="lazy" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                                <Play className="h-4 w-4 text-white drop-shadow" fill="currentColor" />
                              </span>
                            </button>
                          ) : (
                            <div className="h-11 w-[74px] rounded-md bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                              {failed ? <AlertCircle className="h-4 w-4 text-red-400" /> : ready ? <FileVideo className="h-4 w-4 text-amber-400" /> : <Loader2 className="h-4 w-4 text-amber-400 animate-spin" />}
                            </div>
                          )}
                          {ready ? (
                            <button data-testid={`upload-title-${u.id}`} onClick={() => setPlaying({ id: u.id, title: u.title || u.original_filename, playback_url: `${API}/media/${u.id}.mp4`, thumbnail_url: u.thumbnail_url })} className="text-white font-medium truncate max-w-[240px] hover:text-amber-300 transition-colors text-left">{u.title || u.original_filename}</button>
                          ) : (
                            <span className="text-white font-medium truncate max-w-[240px]">{u.title || u.original_filename}</span>
                          )}
                          {u.clip_of && <span data-testid={`upload-clip-badge-${u.id}`} className="shrink-0 inline-flex items-center gap-1 rounded bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-300"><Scissors className="h-3 w-3" /> Clip</span>}
                          {u.clip_format === "vertical" && <span data-testid={`upload-vertical-badge-${u.id}`} className="shrink-0 inline-flex items-center rounded bg-sky-500/10 border border-sky-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-300">9:16</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-400">{fmtSize(u.size_mb)}</td>
                      <td className="px-5 py-3">
                        {ready ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Ready</span>
                        ) : failed ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/30"><AlertCircle className="h-3 w-3" /> Failed</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30"><Loader2 className="h-3 w-3 animate-spin" /> Processing</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{fmtDate(u.created_at)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`upload-play-${u.id}`}
                            onClick={() => ready && setPlaying({ id: u.id, title: u.title || u.original_filename, playback_url: `${API}/media/${u.id}.mp4`, thumbnail_url: u.thumbnail_url })}
                            disabled={!ready}
                            className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors " + (ready ? "bg-amber-500/10 text-amber-300 border-amber-500/30 hover:bg-amber-500/20" : "opacity-40 cursor-not-allowed border-gray-800 text-gray-600")}
                          >
                            <Play className="h-3.5 w-3.5" /> Watch
                          </button>
                          <a
                            data-testid={`upload-download-${u.id}`}
                            href={ready ? buildDownloadUrl(u.playback_url, API, u.id, u.title || u.original_filename) : undefined}
                            download={ready ? safeVideoFilename(u.title || u.original_filename) : undefined}
                            className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-[0.98] " + (ready ? "gold-gradient text-black hover:brightness-110" : "opacity-40 cursor-not-allowed border border-gray-800 text-gray-600 pointer-events-none")}
                          >
                            <Download className="h-3.5 w-3.5" /> Download
                          </a>
                          <button data-testid={`upload-rename-${u.id}`} onClick={() => openRename(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-200 border border-gray-700 hover:text-amber-300 hover:border-amber-500/40 text-xs font-medium transition-colors">
                            <Pencil className="h-3.5 w-3.5" /> Rename
                          </button>
                          {isAdmin && (
                            <button data-testid={`upload-move-${u.id}`} onClick={() => openMove(u, "upload")} disabled={!ready} className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors " + (ready ? "bg-gray-800 text-gray-200 border-gray-700 hover:text-amber-300 hover:border-amber-500/40" : "opacity-40 cursor-not-allowed border-gray-800 text-gray-600")}>
                              <ListPlus className="h-3.5 w-3.5" /> Move
                            </button>
                          )}
                          <button data-testid={`upload-delete-${u.id}`} onClick={() => setConfirmUpload(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 text-xs font-medium transition-colors">
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>
      ) : recordings.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-gray-800 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto">
            <Film className="h-7 w-7 text-amber-400" />
          </div>
          <p className="mt-4 text-white font-medium">No recordings yet</p>
          <p className="text-gray-500 text-sm mt-1 max-w-sm mx-auto">Go live on a stream and end the broadcast — with Auto-Archive enabled, the session shows up here automatically.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {sortedRecordings.map((rec, i) => (
            <motion.div
              key={rec.id}
              data-testid={`recording-card-${rec.id}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
              className="group bg-card border border-gray-800 hover:border-amber-500/40 rounded-2xl overflow-hidden transition-all"
            >
              <button
                data-testid={`recording-play-${rec.id}`}
                onClick={() => rec.playback_url ? setPlaying(rec) : openUrlEdit(rec)}
                className="relative w-full aspect-video bg-black bg-grid flex items-center justify-center overflow-hidden"
              >
                {rec.thumbnail_url ? (
                  <img src={rec.thumbnail_url} alt={rec.title} className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" />
                ) : null}
                <div className="relative h-14 w-14 rounded-full gold-gradient flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                  <Play className="h-6 w-6 text-black ml-0.5" fill="currentColor" />
                </div>
                {!rec.playback_url && <span className="absolute top-2 left-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/70 text-amber-300">Add URL to publish</span>}
                <span className="absolute bottom-2 right-2 text-[11px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-gray-200">{fmtDuration(rec.duration_seconds)}</span>
              </button>
              <div className="p-4">
                <p className="font-semibold text-white text-sm leading-snug line-clamp-2">{rec.title}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-amber-400"><Radio className="h-3 w-3" /> {rec.stream_title}</span>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800/70">
                  <div className="flex items-center gap-3 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDuration(rec.duration_seconds)}</span>
                    <span className="inline-flex items-center gap-1"><HardDrive className="h-3 w-3" /> {fmtSize(rec.size_mb)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {rec.playback_url && (
                      <a data-testid={`recording-download-${rec.id}`} href={`${API}/recordings/${rec.id}/download?name=${encodeURIComponent(safeVideoFilename(rec.title))}`} download={safeVideoFilename(rec.title)} title="Download video" className="h-7 w-7 rounded-lg flex items-center justify-center text-amber-400 hover:bg-amber-500/10 transition-colors">
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {rec.playback_url && (
                      <button data-testid={`recording-clip-${rec.id}`} onClick={() => setClipRec(rec)} title="Make a clip" className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors">
                        <Scissors className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button data-testid={`recording-set-url-${rec.id}`} onClick={() => openUrlEdit(rec)} title="Set playback URL" className={"h-7 w-7 rounded-lg flex items-center justify-center transition-colors " + (rec.playback_url ? "text-emerald-400 hover:bg-emerald-500/10" : "text-gray-500 hover:text-amber-400 hover:bg-amber-500/10")}>
                      <Link2 className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`recording-move-${rec.id}`} onClick={() => openMove(rec, "recording")} title="Add to playlist" className="h-7 px-2 rounded-lg flex items-center gap-1 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors text-[11px] font-medium">
                      <ListPlus className="h-3.5 w-3.5" /> Playlist
                    </button>
                    <button data-testid={`recording-delete-${rec.id}`} onClick={() => setConfirmRec(rec)} className="h-7 w-7 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-gray-600 mt-2 flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(rec.created_at)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <ClipDialog recording={clipRec} onClose={() => setClipRec(null)} onCreated={() => { loadUploads(); loadQuota(); }} />

      {/* Inline player modal */}
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-gray-800 max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="font-display text-lg">{playing?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black mt-3">
            {playing && <HlsPlayer key={playing.id} src={playing.playback_url} poster={playing.thumbnail_url} autoPlay controls />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Set playback URL */}
      <Dialog open={!!urlEdit} onOpenChange={(o) => !o && setUrlEdit(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Set playback URL</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-gray-300 text-sm">Video URL (.m3u8 HLS or .mp4)</Label>
            <Input data-testid="recording-url-input" value={urlValue} onChange={(e) => setUrlValue(e.target.value)} placeholder="https://streamworship.org/memfs/....m3u8" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 font-mono text-sm" />
            <p className="text-[11px] text-gray-500">Paste the recording URL from your Restreamer engine (or any MP4/HLS link). Once set, this video appears in your public website library.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setUrlEdit(null)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-800">Cancel</button>
              <button data-testid="save-recording-url" onClick={saveUrl} disabled={savingUrl} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
                {savingUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Website embed code */}
      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><Code2 className="h-5 w-5 text-amber-400" /> Embed your video library</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">Match the gallery to your website, then paste this code into any page. Visitors get a branded gallery and can pick any video (or a live stream) to watch.</p>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Accent color</Label>
                <div className="flex items-center gap-2">
                  <input data-testid="theme-accent" type="color" value={theme.accent} onChange={(e) => setTheme({ ...theme, accent: e.target.value })} className="h-9 w-10 rounded bg-transparent border border-gray-800 cursor-pointer" />
                  <Input value={theme.accent} onChange={(e) => setTheme({ ...theme, accent: e.target.value })} className="bg-gray-950 border-gray-800 font-mono text-xs h-9" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-300 text-xs">Bar text color</Label>
                <div className="flex items-center gap-2">
                  <input data-testid="theme-text" type="color" value={theme.text} onChange={(e) => setTheme({ ...theme, text: e.target.value })} className="h-9 w-10 rounded bg-transparent border border-gray-800 cursor-pointer" />
                  <Input value={theme.text} onChange={(e) => setTheme({ ...theme, text: e.target.value })} className="bg-gray-950 border-gray-800 font-mono text-xs h-9" />
                </div>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-gray-300 text-xs">Title (shown in the bar)</Label>
                <Input data-testid="theme-title" value={theme.title} onChange={(e) => setTheme({ ...theme, title: e.target.value })} placeholder="Your church name" className="bg-gray-950 border-gray-800 text-sm h-9" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-gray-300 text-xs">Playlist position</Label>
                <div className="flex gap-2">
                  {["side", "below"].map((l) => (
                    <button key={l} data-testid={`theme-layout-${l}`} onClick={() => setTheme({ ...theme, layout: l })}
                      className={"px-3 h-9 rounded-lg text-sm border capitalize " + (theme.layout === l ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-card text-gray-400 border-gray-800 hover:text-white")}>
                      {l === "side" ? "Beside video" : "Below video"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-gray-300 text-xs">Player size</Label>
                <div className="flex gap-2">
                  {[["640", "640×360"], ["960", "960×540"], ["full", "Full width"]].map(([v, lbl]) => (
                    <button key={v} data-testid={`embed-size-${v}`} onClick={() => setEmbedSize(v)}
                      className={"px-3 h-9 rounded-lg text-sm border " + (embedSize === v ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-card text-gray-400 border-gray-800 hover:text-white")}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500">Stays 16:9 and shrinks to fit smaller screens automatically.</p>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-800">
              <div data-testid="theme-preview-bar" className="flex items-center gap-2 px-3 py-2 text-sm font-semibold" style={{ background: theme.accent, color: theme.text }}>
                <Radio className="h-4 w-4" /> {theme.title || "Video Library"}
              </div>
              <div className="h-16 bg-black flex items-center justify-center text-gray-600 text-xs">video preview</div>
            </div>

            <div className="rounded-xl border border-gray-800 bg-gray-950 p-3">
              <code data-testid="library-embed-code" className="block text-xs text-gray-300 break-all font-mono whitespace-pre-wrap">{embedCode}</code>
            </div>
            <div className="flex items-center justify-between gap-2">
              <a data-testid="library-preview-link" href={embedSrc} target="_blank" rel="noreferrer" className="text-sm text-amber-400 hover:underline">Preview library →</a>
              <button data-testid="copy-library-embed" onClick={copyEmbed} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy code"}
              </button>
            </div>
            <p className="text-[11px] text-gray-500">Only videos with a saved playback URL appear in the library.</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Rename video</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <Label className="text-gray-300 text-sm">Video title</Label>
            <Input
              data-testid="rename-input"
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !savingRename && saveRename()}
              placeholder="e.g. Sunday Morning Service — June 8"
              className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40"
            />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setRenameTarget(null)} className="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:text-white text-sm transition-colors">Cancel</button>
              <button data-testid="rename-save" onClick={saveRename} disabled={savingRename} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:brightness-110 transition-all disabled:opacity-60 text-sm">
                {savingRename ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmRec}
        onOpenChange={(o) => !o && setConfirmRec(null)}
        title="Delete this video?"
        description={confirmRec ? `"${confirmRec.title}" will be permanently removed from your archive${confirmRec.storage === "r2" || (confirmRec.playback_url || "").includes("/api/media/") ? " and deleted from Cloudflare R2 storage" : ""}. This cannot be undone.` : ""}
        confirmLabel="Delete permanently"
        busy={deleting}
        onConfirm={() => confirmRec && remove(confirmRec)}
      />

      <ConfirmDialog
        open={!!confirmUpload}
        onOpenChange={(o) => !o && setConfirmUpload(null)}
        title="Delete this uploaded video?"
        description={confirmUpload ? `"${confirmUpload.title || confirmUpload.original_filename}" will be permanently removed from your library and deleted from Cloudflare R2 storage. This cannot be undone.` : ""}
        confirmLabel="Delete permanently"
        busy={deletingUpload}
        onConfirm={() => confirmUpload && removeUpload(confirmUpload)}
      />

      {/* Admin bulk upload */}
      <Dialog open={bulkOpen} onOpenChange={(o) => (o ? setBulkOpen(true) : closeBulk())}>
        <DialogContent aria-describedby={undefined} className="bg-card border-gray-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <Layers className="h-5 w-5 text-amber-400" /> Bulk upload videos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-1">
            {/* Step 1: choose target (channel or account) */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">1. Where should these videos go?</Label>
              <div className="inline-flex rounded-lg border border-gray-800 bg-gray-950 p-1" data-testid="bulk-mode-toggle">
                <button
                  data-testid="bulk-mode-channel"
                  onClick={() => !bulkRunning && setBulkMode("channel")}
                  disabled={bulkRunning}
                  className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors " + (bulkMode === "channel" ? "gold-gradient text-black" : "text-gray-400 hover:text-white")}
                >
                  <Tv className="h-3.5 w-3.5" /> Channel
                </button>
                <button
                  data-testid="bulk-mode-account"
                  onClick={() => !bulkRunning && setBulkMode("account")}
                  disabled={bulkRunning}
                  className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors " + (bulkMode === "account" ? "gold-gradient text-black" : "text-gray-400 hover:text-white")}
                >
                  <Users className="h-3.5 w-3.5" /> Customer account
                </button>
              </div>

              {bulkMode === "channel" ? (
                <>
                  <Select value={bulkChannel} onValueChange={setBulkChannel} disabled={bulkRunning}>
                    <SelectTrigger data-testid="bulk-channel-select" className="bg-gray-950 border-gray-800 focus:ring-amber-500/40">
                      <SelectValue placeholder="Select a channel…" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-950 border-gray-800 text-white">
                      {churches.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No channels yet — create one in Channels.</div>
                      )}
                      {churches.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`bulk-channel-option-${c.id}`} className="focus:bg-amber-500/10">
                          <span className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.accent_color || "#3B82F6" }} />
                            {c.name} <span className="text-gray-500 text-xs">· {c.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {bulkChannel && !channelHasAccount && (
                    <p data-testid="bulk-no-account-warning" className="text-xs flex items-start gap-1.5 text-amber-400">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      This channel has no linked account yet. Link an account to it on the Channels page before uploading.
                    </p>
                  )}
                  {bulkChannel && channelHasAccount && (
                    <p className="text-[11px] text-gray-500">Videos upload to this account's library and appear in its <span className="text-gray-300">All Videos</span> list, ready to organize.</p>
                  )}
                </>
              ) : (
                <>
                  <Select value={bulkAccount} onValueChange={setBulkAccount} disabled={bulkRunning}>
                    <SelectTrigger data-testid="bulk-account-select" className="bg-gray-950 border-gray-800 focus:ring-amber-500/40">
                      <SelectValue placeholder="Select a customer account…" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-950 border-gray-800 text-white max-h-[280px]">
                      {accounts.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No customer accounts found.</div>
                      )}
                      {accounts.map((a) => (
                        <SelectItem key={a.user_id} value={a.user_id} data-testid={`bulk-account-option-${a.user_id}`} className="focus:bg-amber-500/10">
                          {(a.organization || a.name || a.email)}<span className="text-gray-500"> · {a.email}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {bulkAccount && (
                    <p className="text-[11px] text-gray-500">Videos upload straight to this account's library and its <span className="text-gray-300">All Videos</span> list.</p>
                  )}
                </>
              )}
            </div>

            {/* Step 2: add files */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">2. Add video files</Label>
              <input
                data-testid="bulk-file-input"
                type="file"
                accept="video/mp4,.mp4"
                multiple
                onChange={addBulkFiles}
                className="hidden"
                id="bulk-upload-input"
                disabled={bulkRunning || !bulkTargetReady}
              />
              <label
                htmlFor="bulk-upload-input"
                data-testid="bulk-add-files-label"
                className={"w-full rounded-xl border border-dashed flex flex-col items-center justify-center gap-2 py-8 transition-colors " +
                  (bulkRunning || !bulkTargetReady
                    ? "border-gray-800 text-gray-600 cursor-not-allowed"
                    : "border-amber-500/40 text-amber-300 hover:bg-amber-500/[0.06] cursor-pointer")}
              >
                <Plus className="h-6 w-6" />
                <span className="text-sm font-medium">Select multiple MP4 files</span>
                <span className="text-[11px] text-gray-500">Up to 5 GB each · they upload one after another</span>
              </label>
            </div>

            {/* File list with per-file progress + status */}
            {bulkFiles.length > 0 && (
              <div data-testid="bulk-file-list" className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                {bulkFiles.map((f) => (
                  <div key={f.id} data-testid={`bulk-file-${f.id}`} className="rounded-lg border border-gray-800 bg-gray-950/60 p-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <FileVideo className="h-4 w-4 text-gray-500 shrink-0" />
                        <span className="text-sm text-white truncate">{f.name}</span>
                        <span className="text-[11px] text-gray-500 shrink-0">{fmtSize(Math.round(f.size / (1024 * 1024)))}</span>
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        {f.status === "queued" && <span data-testid={`bulk-status-${f.id}`} className="text-[11px] text-gray-500">Queued</span>}
                        {f.status === "uploading" && <span data-testid={`bulk-status-${f.id}`} className="text-[11px] text-amber-300 font-mono">{f.progress}%</span>}
                        {f.status === "complete" && <span data-testid={`bulk-status-${f.id}`} className="text-[11px] text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Complete</span>}
                        {f.status === "failed" && <span data-testid={`bulk-status-${f.id}`} className="text-[11px] text-red-400 inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" /> Failed</span>}
                        {!bulkRunning && (f.status === "queued" || f.status === "failed") && (
                          <button data-testid={`bulk-remove-${f.id}`} onClick={() => removeBulkFile(f.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-gray-800 overflow-hidden">
                      <div
                        className={"h-full rounded-full transition-all duration-200 " +
                          (f.status === "complete" ? "bg-emerald-500" : f.status === "failed" ? "bg-red-500" : "gold-gradient")}
                        style={{ width: `${f.status === "failed" ? 100 : f.progress}%` }}
                      />
                    </div>
                    {f.status === "failed" && f.error && (
                      <p className="text-[11px] text-red-400/80 mt-1.5">{f.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="bulk-close-button" onClick={closeBulk} disabled={bulkRunning} className="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:text-white text-sm transition-colors disabled:opacity-50">
                {bulkFiles.some((f) => f.status === "complete") ? "Done" : "Cancel"}
              </button>
              <button
                data-testid="bulk-start-button"
                onClick={startBulkUpload}
                disabled={bulkRunning || !bulkTargetReady || !bulkFiles.some((f) => f.status === "queued" || f.status === "failed")}
                className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:brightness-110 transition-all disabled:opacity-50 text-sm"
              >
                {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                {bulkRunning ? "Uploading…" : "Start upload"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: move a video into one of its account's playlists */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && !moving && setMoveTarget(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <ListPlus className="h-5 w-5 text-amber-400" /> Move to playlist
            </DialogTitle>
          </DialogHeader>
          {moveTarget && (
            <div className="space-y-4 py-1">
              <div className="rounded-lg border border-gray-800 bg-gray-950/60 p-3 flex items-center gap-3">
                <FileVideo className="h-4 w-4 text-gray-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{moveTarget.title || moveTarget.original_filename}</p>
                  <p className="text-[11px] text-gray-500 truncate">Account: {moveTarget.owner_name || "—"}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Add this video to a playlist</Label>
                <Select value={moveSelected} onValueChange={setMoveSelected} disabled={moving}>
                  <SelectTrigger data-testid="move-playlist-select" className="bg-gray-950 border-gray-800 focus:ring-amber-500/40">
                    <SelectValue placeholder="Choose a playlist…" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-950 border-gray-800 text-white max-h-[260px]">
                    {movePlaylists.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`move-playlist-option-${p.id}`} className="focus:bg-amber-500/10">
                        {p.name} <span className="text-gray-500 text-xs">· {p.item_count ?? (p.items?.length || 0)} video{(p.item_count ?? (p.items?.length || 0)) === 1 ? "" : "s"}</span>
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__" data-testid="move-playlist-new-option" className="focus:bg-amber-500/10 text-amber-300">
                      <span className="inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Create a new playlist…</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {moveSelected === "__new__" && (
                  <Input
                    data-testid="move-new-playlist-name"
                    autoFocus
                    value={moveNewName}
                    onChange={(e) => setMoveNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !moving && doMove()}
                    placeholder="e.g. Sunday Sermons — Summer 2026"
                    className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40"
                  />
                )}
                {movePlaylists.length === 0 && moveSelected !== "__new__" && (
                  <p className="text-[11px] text-gray-500">This account has no playlists yet — create one above.</p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button data-testid="move-cancel-button" onClick={() => setMoveTarget(null)} disabled={moving} className="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:text-white text-sm transition-colors disabled:opacity-50">Cancel</button>
                <button
                  data-testid="move-confirm-button"
                  onClick={doMove}
                  disabled={moving || !moveSelected || (moveSelected === "__new__" && !moveNewName.trim())}
                  className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:brightness-110 transition-all disabled:opacity-50 text-sm"
                >
                  {moving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListPlus className="h-4 w-4" />} Add to playlist
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
